import React, { useEffect, useState, useRef } from 'react';
import { useLiceos } from '../../../../application/liceos/hooks/useLiceos';
import ToolbarComponent from '../../../ui/section';
import Input from '../../../ui/input';
import { Row, Col } from '../../../ui/grid';
import { PrimaryButton } from '../../../ui/button';
import { ModalConfirm } from '../../../ui/modal2';
import Loader from '../../../ui/loader';

const NewUserLiceoPage = (props) => {
    const {
        createUserComplete,
        updateUser,
        getUserById,
        getLiceoUsers,
        getUserTypes,
        isLoading,
        error
    } = useLiceos();

    const liceoId = props.match?.params?.liceoId;
    const userId = props.match?.params?.userId;
    const isEdit = !!userId;

    // Función para detectar si un ID es un UUID
    const isUUID = (id) => {
        if (!id || typeof id !== 'string') return false;
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(id);
    };

    const [formData, setFormData] = useState({
        email: '',
        username: '',
        firstName: '',
        lastName: '',
        userType: 'estudiante',
        verifyEmail: true,
        autoPassword: true
    });
    const [userTypes, setUserTypes] = useState(['estudiante', 'docente', 'director', 'coach']);
    const [confirmSave, setConfirmSave] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState(null);
    const userTypesLoadedRef = useRef(false);

    // Cargar tipos de usuario solo una vez al montar
    useEffect(() => {
        if (userTypesLoadedRef.current) return;
        userTypesLoadedRef.current = true;
        
        const loadUserTypes = async () => {
            try {
                const types = await getUserTypes();
                if (Array.isArray(types) && types.length > 0) {
                    setUserTypes(types);
                }
            } catch (err) {
                console.error('Error al cargar tipos de usuario:', err);
            }
        };
        
        loadUserTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Solo ejecutar una vez al montar

    // Cargar usuario solo si estamos editando
    useEffect(() => {
        if (!userId || !liceoId) return;
        
        const loadUser = async () => {
            setLoading(true);
            setLocalError(null);
            try {
                let data = null;
                
                // Si el ID es un UUID, buscar en la lista de usuarios del liceo
                if (isUUID(userId)) {
                    console.log('🔍 ID es UUID, buscando en usuarios del liceo...');
                    const users = await getLiceoUsers(liceoId);
                    const userArray = Array.isArray(users) ? users : (users?.users || []);
                    data = userArray.find(u => (u.id === userId || u._id === userId));
                    
                    if (!data) {
                        console.error('❌ Usuario no encontrado en el liceo con UUID:', userId);
                        setLocalError('Usuario no encontrado');
                        setLoading(false);
                        return;
                    }
                } else {
                    // Si es ObjectId, usar getUserById normalmente
                    console.log('🔍 ID es ObjectId, usando getUserById...');
                    data = await getUserById(userId);
                }
                
                if (data) {
                    setFormData({
                        email: data.email || '',
                        username: data.username || '',
                        firstName: data.firstName || data.name?.split(' ')[0] || '',
                        lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || '',
                        userType: data.userType || 'estudiante',
                        verifyEmail: data.verifyEmail !== undefined ? data.verifyEmail : true,
                        autoPassword: data.autoPassword !== undefined ? data.autoPassword : true
                    });
                }
            } catch (err) {
                console.error('Error al cargar usuario:', err);
                setLocalError('Error al cargar el usuario');
            } finally {
                setLoading(false);
            }
        };
        
        loadUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, liceoId]); // Solo cuando cambia userId o liceoId

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        setConfirmSave(false);
        setLoading(true);
        try {
            let result = null;
            if (isEdit) {
                result = await updateUser(userId, {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    userType: formData.userType
                });
            } else {
                // Validar campos requeridos
                if (!formData.email || !formData.firstName || !formData.lastName) {
                    console.error('Faltan campos requeridos');
                    return;
                }
                result = await createUserComplete({
                    ...formData,
                    liceoId: liceoId
                });
            }
            
            // Solo redirigir si la operación fue exitosa
            if (result !== null && result !== undefined) {
                props.history.push(`/academia/liceos/${liceoId}`);
            }
        } catch (err) {
            console.error('Error al guardar usuario:', err);
        } finally {
            setLoading(false);
        }
    };

    const getUserTypeLabel = (type) => {
        const labels = {
            estudiante: 'Estudiante',
            docente: 'Docente',
            director: 'Director',
            coach: 'Coach'
        };
        return labels[type] || type;
    };

    if (loading || isLoading) {
        return (
            <ToolbarComponent label={isEdit ? "Editar Usuario" : "Nuevo Usuario"} showBackButton={true} onBackClick={() => props.history.goBack()}>
                <Loader text="Cargando..." />
            </ToolbarComponent>
        );
    }

    return (
        <ToolbarComponent label={isEdit ? "Editar Usuario" : "Nuevo Usuario"} showBackButton={true} onBackClick={() => props.history.goBack()}>
            {(error || localError) && (
                <div style={{ padding: '10px', background: '#FEE2E2', color: '#DC2626', borderRadius: '4px', marginBottom: '16px' }}>
                    {localError || error}
                </div>
            )}
            <Row padding="0 20px">
                <br />
                <Col md={6}>
                    <Input 
                        label="Nombre" 
                        type="text" 
                        onChange={(e) => handleChange('firstName', e.target.value)} 
                        value={formData.firstName} 
                    />
                </Col>
                <Col md={6}>
                    <Input 
                        label="Apellido" 
                        type="text" 
                        onChange={(e) => handleChange('lastName', e.target.value)} 
                        value={formData.lastName} 
                    />
                </Col>
                <Col md={6}>
                    <Input 
                        label="Email" 
                        type="email" 
                        onChange={(e) => handleChange('email', e.target.value)} 
                        value={formData.email} 
                        disabled={isEdit}
                    />
                </Col>
                <Col md={6}>
                    <Input 
                        label="Usuario" 
                        type="text" 
                        onChange={(e) => handleChange('username', e.target.value)} 
                        value={formData.username} 
                        disabled={isEdit}
                    />
                </Col>
                <Col md={12}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#6B7280' }}>
                        Tipo de Usuario
                    </label>
                    <select
                        value={formData.userType}
                        onChange={(e) => handleChange('userType', e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    >
                        {userTypes.map(type => (
                            <option key={type} value={type}>
                                {getUserTypeLabel(type)}
                            </option>
                        ))}
                    </select>
                </Col>
                {!isEdit && (
                    <>
                        <Col md={6}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.verifyEmail}
                                    onChange={(e) => handleChange('verifyEmail', e.target.checked)}
                                />
                                <span style={{ fontSize: '14px', color: '#374151' }}>Verificar email</span>
                            </label>
                        </Col>
                        <Col md={6}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.autoPassword}
                                    onChange={(e) => handleChange('autoPassword', e.target.checked)}
                                />
                                <span style={{ fontSize: '14px', color: '#374151' }}>Generar contraseña automáticamente</span>
                            </label>
                        </Col>
                    </>
                )}
                <Col md={12} className="text-right">
                    <br />
                    <PrimaryButton 
                        onClick={() => setConfirmSave(true)} 
                        text={isEdit ? "Actualizar usuario" : "Crear usuario"} 
                    />
                </Col>
            </Row>
            <ModalConfirm 
                isOpen={confirmSave} 
                onClose={() => setConfirmSave(false)} 
                onAccept={handleSave}
                title={isEdit ? "¿Actualizar usuario?" : "¿Crear usuario?"}
                message={isEdit ? "Se actualizará la información del usuario." : "Se creará un nuevo usuario en todos los sistemas."}
            />
        </ToolbarComponent>
    );
};

export default NewUserLiceoPage;


